import type { PublicSessionDemoBlock } from "@workspace/types";
import { inject, injectable } from "inversify";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import {
  endCoachFreeze,
  getPublicTrainingSession,
  pausePublicTrainingSession,
  resumePublicTrainingSession,
} from "../api/training-sessions";
import { extractApiErrorCode, extractApiErrorMessage } from "../api/error";
import { trackDemoEvent } from "../analytics/events";
import { DEMO_DURATION_MS } from "../constants/demo";
import { LandingServiceID } from "./symbols";
import type {
  DemoAppMessageEvent,
  DemoCallHost,
  IDemoCallControlManager,
  IDemoClockManager,
  PauseKind,
} from "@/lib/types/demo-session";

type HoldKind = NonNullable<PauseKind>;

const BOUNDARY_HARD_CAP_MS = 8000;
const PAUSE_RECOVERY_GRACE_MS = 3000;
// The server caps a freeze at 240s; this only guards a lost `coach_freeze_ended`.
const FREEZE_CAP_MS = 240_000;
const FREEZE_RECOVERY_GRACE_MS = 5000;
const RECOVERY_RETRY_MS: Record<HoldKind, number> = {
  rep: PAUSE_RECOVERY_GRACE_MS,
  coach_freeze: FREEZE_RECOVERY_GRACE_MS,
};

@injectable()
export class DemoCallControlStore implements IDemoCallControlManager {
  pauseKind: PauseKind = null;
  coachLine: string | null = null;
  isPauseInFlight: boolean = false;
  isFreezeEnding: boolean = false;

  private host: DemoCallHost | null = null;
  /** Epoch ms: REST (`…Z`) and app-message (`+00:00`) serialize the same instant differently. */
  private lastResumedPausedAt: number | null = null;
  private lastEndedFreezePausedAt: number | null = null;
  private freezeStartedAtMs: number | null = null;
  private recoveryTimers: Record<HoldKind, ReturnType<typeof setTimeout> | null> = {
    rep: null,
    coach_freeze: null,
  };
  private boundaryDispose: (() => void) | null = null;
  private boundaryHardCap: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @inject(LandingServiceID.DemoClockStore)
    private readonly demoClock: IDemoClockManager,
  ) {
    makeAutoObservable<
      DemoCallControlStore,
      | "host"
      | "demoClock"
      | "recoveryTimers"
      | "boundaryDispose"
      | "boundaryHardCap"
    >(
      this,
      {
        host: false,
        demoClock: false,
        recoveryTimers: false,
        boundaryDispose: false,
        boundaryHardCap: false,
      },
      { autoBind: true },
    );
  }

  bindHost(host: DemoCallHost): void {
    this.host = host;
  }

  handleAppMessage(message: DemoAppMessageEvent): void {
    switch (message.event) {
      case "session_ending":
        if (message.deadline_at) this.demoClock.setDeadline(message.deadline_at);
        this.demoClock.setEnding();
        break;
      case "session_ended":
        // end() requires a live call; a frame while connecting or after finish is ignored.
        if (this.host?.isCallLive()) void this.host.endCall().catch(() => {});
        break;
      case "deadline_updated":
        this.demoClock.setDeadline(message.deadline_at, message.reason === "auto_extend");
        // An extension can land after the boundary already armed the hard cap.
        if (this.demoClock.remainingMs > 0) this.disarmBoundaryHardCap();
        this.demoClock.clearStaleEnding();
        break;
      case "session_paused":
        // Ignore a late keepalive for the pause we already resumed.
        if (
          this.lastResumedPausedAt !== null &&
          Date.parse(message.paused_at) === this.lastResumedPausedAt
        ) {
          return;
        }
        this.beginPauseTracking(message.paused_at, message.auto_resume_at);
        break;
      case "session_resumed":
        this.applyResume(message.reason, message.deadline_at);
        break;
      case "coach_freeze_started":
        this.applyFreezeStart(message.paused_at, message.coach_line);
        break;
      case "coach_freeze_ended":
        this.applyFreezeEnd(message.reason, message.deadline_at);
        break;
    }
  }

  /** Callers gate connecting/wrap-up states. */
  async pauseCall(): Promise<void> {
    const host = this.host;
    const sessionId = host?.getDemoSessionId();
    if (!host || !sessionId) return;
    if (!host.isCallLive()) return;
    if (this.demoClock.paused || this.demoClock.ending) return;
    if (this.isPauseInFlight) return;

    runInAction(() => {
      this.isPauseInFlight = true;
    });

    host.setLocalAudio(false);
    try {
      await host.cancelAvatarTasks();
    } catch {
      // Best-effort — pausing still proceeds with nothing to cancel.
    }

    try {
      const data = await pausePublicTrainingSession(sessionId);
      this.applyPause(data.demo);
      if (this.pauseKind === "coach_freeze") host.restoreLocalAudio();
    } catch (error) {
      if (extractApiErrorCode(error) === "session_already_paused") {
        // Already paused elsewhere — hydrate now instead of waiting for the keepalive.
        try {
          const fresh = await getPublicTrainingSession(sessionId);
          const demo = fresh.demo;
          if (demo?.pause_kind === "coach_freeze" && demo.paused_at) {
            host.setDemoInfo(demo);
            host.restoreLocalAudio();
            this.applyFreezeStart(demo.paused_at, demo.coach_line ?? "");
          } else {
            this.applyPause(demo);
          }
        } catch {
          host.restoreLocalAudio();
        }
        return;
      }

      host.restoreLocalAudio();
      trackDemoEvent("session_error", {
        sessionId,
        errorMessage: extractApiErrorMessage(error, "Failed to pause the call"),
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isPauseInFlight = false;
      });
    }
  }

  async resumeCall(): Promise<void> {
    const host = this.host;
    const sessionId = host?.getDemoSessionId();
    if (!host || !sessionId) return;
    if (!this.demoClock.paused || this.pauseKind !== "rep") return;
    if (this.isPauseInFlight) return;

    runInAction(() => {
      this.isPauseInFlight = true;
    });

    try {
      const data = await resumePublicTrainingSession(sessionId);
      if (data.demo) host.setDemoInfo(data.demo);
      this.applyResume("manual", data.demo?.deadline_at ?? null);
    } catch (error) {
      trackDemoEvent("session_error", {
        sessionId,
        errorMessage: extractApiErrorMessage(error, "Failed to resume the call"),
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isPauseInFlight = false;
      });
    }
  }

  /** "Back to the call" or Esc. Stops the coach's in-flight line first, like pauseCall() does the avatar's. */
  async endFreeze(): Promise<void> {
    const host = this.host;
    const sessionId = host?.getDemoSessionId();
    if (!host || !sessionId) return;
    if (this.pauseKind !== "coach_freeze") return;
    if (this.isFreezeEnding) return;

    runInAction(() => {
      this.isFreezeEnding = true;
    });

    try {
      await host.cancelAvatarTasks();
    } catch {
      // Best-effort — ending still proceeds with nothing to cancel.
    }

    try {
      const data = await endCoachFreeze(sessionId);
      if (data.demo) host.setDemoInfo(data.demo);
      this.applyFreezeEnd("manual", data.demo?.deadline_at ?? null);
    } catch (error) {
      if (extractApiErrorCode(error) === "not_in_coach_freeze") return;
      trackDemoEvent("session_error", {
        sessionId,
        errorMessage: extractApiErrorMessage(error, "Failed to end the coach freeze"),
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isFreezeEnding = false;
      });
    }
  }

  /** `deadline_at` may not be written yet — fall back to a local countdown. */
  async syncClockFromServer(isStale: () => boolean = () => false): Promise<void> {
    const host = this.host;
    const sessionId = host?.getDemoSessionId();
    if (!host || !sessionId) return;

    try {
      const fresh = await getPublicTrainingSession(sessionId);
      if (isStale()) return;
      if (fresh.demo) {
        const demo = fresh.demo;
        host.setDemoInfo(demo);
        // Deadline first — pause() freezes off the deadline-derived remainingMs.
        if (demo.deadline_at) {
          this.demoClock.setDeadline(demo.deadline_at);
        }
        if (demo.pause_kind === "coach_freeze" && demo.paused_at) {
          // The GET can land before the reconnect's coach_freeze_ended.
          const pausedAtMs = Date.parse(demo.paused_at);
          this.applyFreezeStart(
            demo.paused_at,
            demo.coach_line ?? "",
            Number.isFinite(pausedAtMs) ? pausedAtMs : Date.now(),
          );
        } else if (demo.paused_at && demo.auto_resume_at) {
          this.beginPauseTracking(demo.paused_at, demo.auto_resume_at);
        }
        if (demo.deadline_at) return;
      }
    } catch {
      // Fall through to the local fallback below.
    }

    if (isStale()) return;
    this.demoClock.setLocalFallback(DEMO_DURATION_MS);
  }

  watchBoundary(): void {
    this.disarmBoundaryWatch();
    this.boundaryDispose = reaction(
      () => this.demoClock.remainingMs,
      (remainingMs) => {
        if (remainingMs > 0) return;
        if (!this.demoClock.ending) this.demoClock.setEnding();
        if (this.boundaryHardCap) return;
        this.boundaryHardCap = setTimeout(() => {
          this.boundaryHardCap = null;
          const host = this.host;
          if (host?.isCallLive()) void host.endCall().catch(() => {});
        }, BOUNDARY_HARD_CAP_MS);
      },
    );
  }

  stop(): void {
    this.disarmBoundaryWatch();
    this.disarmRecoveryWatch("rep");
    this.disarmRecoveryWatch("coach_freeze");
  }

  reset(): void {
    this.stop();
    this.lastResumedPausedAt = null;
    this.lastEndedFreezePausedAt = null;
    this.freezeStartedAtMs = null;
    runInAction(() => {
      this.pauseKind = null;
      this.coachLine = null;
      this.isPauseInFlight = false;
      this.isFreezeEnding = false;
    });
  }

  private applyPause(demo: PublicSessionDemoBlock | null | undefined): void {
    if (!demo?.paused_at || !demo?.auto_resume_at) return;
    this.host?.setDemoInfo(demo);
    this.beginPauseTracking(demo.paused_at, demo.auto_resume_at);
  }

  /** A freeze owns the clock pause; never override it. */
  private beginPauseTracking(pausedAt: string, autoResumeAt: string): void {
    if (this.pauseKind === "coach_freeze") return;
    runInAction(() => {
      this.pauseKind = "rep";
    });
    this.demoClock.pause(pausedAt, autoResumeAt);
    const delayMs =
      Math.max(0, new Date(autoResumeAt).getTime() - Date.now()) + PAUSE_RECOVERY_GRACE_MS;
    this.armRecoveryWatch("rep", delayMs);
  }

  private applyResume(reason: "manual" | "auto", deadlineAt: string | null): void {
    if (!this.demoClock.paused || this.pauseKind !== "rep") return;
    this.lastResumedPausedAt = this.demoClock.pausedAt ? Date.parse(this.demoClock.pausedAt) : null;
    this.disarmRecoveryWatch("rep");
    runInAction(() => {
      this.pauseKind = null;
    });
    this.host?.restoreLocalAudio();
    this.demoClock.resume(reason);
    if (deadlineAt) this.demoClock.setDeadline(deadlineAt);
    this.demoClock.clearStaleEnding();
  }

  /** Can land late for an ended freeze — dropped by paused_at. */
  private applyFreezeStart(
    pausedAt: string,
    coachLine: string,
    startedAtMs: number = Date.now(),
  ): void {
    if (this.pauseKind === "rep") return;
    const pausedAtMs = Date.parse(pausedAt);
    if (this.lastEndedFreezePausedAt !== null && pausedAtMs === this.lastEndedFreezePausedAt) return;
    if (this.pauseKind === "coach_freeze") {
      runInAction(() => {
        this.coachLine = coachLine;
      });
      return;
    }
    runInAction(() => {
      this.pauseKind = "coach_freeze";
      this.coachLine = coachLine;
    });
    this.demoClock.pause(pausedAt, null);
    this.armFreezeRecoveryWatch(pausedAt);
    this.freezeStartedAtMs = startedAtMs;
    trackDemoEvent("demo_freeze_started", { sessionId: this.host?.getDemoSessionId() ?? undefined });
  }

  private applyFreezeEnd(reason: "manual" | "auto", deadlineAt: string | null): void {
    if (!this.demoClock.paused || this.pauseKind !== "coach_freeze") return;
    this.disarmRecoveryWatch("coach_freeze");
    this.lastEndedFreezePausedAt = this.demoClock.pausedAt ? Date.parse(this.demoClock.pausedAt) : null;
    const heldMs = this.freezeStartedAtMs !== null ? Date.now() - this.freezeStartedAtMs : undefined;
    this.freezeStartedAtMs = null;
    runInAction(() => {
      this.pauseKind = null;
      this.coachLine = null;
    });
    this.host?.restoreLocalAudio();
    this.demoClock.resumeFromFreeze();
    if (deadlineAt) this.demoClock.setDeadline(deadlineAt);
    this.demoClock.clearStaleEnding();
    trackDemoEvent("demo_freeze_ended", {
      sessionId: this.host?.getDemoSessionId() ?? undefined,
      reason,
      heldMs,
    });
  }

  /** Fixed target time, so keepalive re-arms don't push it out. */
  private armFreezeRecoveryWatch(pausedAt: string): void {
    const target = Date.parse(pausedAt) + FREEZE_CAP_MS + FREEZE_RECOVERY_GRACE_MS;
    this.armRecoveryWatch("coach_freeze", Math.max(FREEZE_RECOVERY_GRACE_MS, target - Date.now()));
  }

  private armRecoveryWatch(kind: HoldKind, delayMs: number): void {
    this.disarmRecoveryWatch(kind);
    this.recoveryTimers[kind] = setTimeout(() => {
      this.recoveryTimers[kind] = null;
      void this.reconcileStaleHold(kind);
    }, delayMs);
  }

  private disarmRecoveryWatch(kind: HoldKind): void {
    const timer = this.recoveryTimers[kind];
    if (timer) {
      clearTimeout(timer);
      this.recoveryTimers[kind] = null;
    }
  }

  private isHeldAs(kind: HoldKind): boolean {
    return this.demoClock.paused && this.pauseKind === kind;
  }

  /** Guards a lost `session_resumed`/`coach_freeze_ended`; one timer, re-armed while the server has no deadline. */
  private async reconcileStaleHold(kind: HoldKind): Promise<void> {
    const host = this.host;
    const sessionId = host?.getDemoSessionId();
    if (!host || !sessionId || !this.isHeldAs(kind)) return;

    try {
      const fresh = await getPublicTrainingSession(sessionId);
      if (!this.isHeldAs(kind)) return;

      // Server reports paused_at null once completed — check completed_at first.
      if (fresh.completed_at) {
        this.demoClock.reset();
        this.disarmRecoveryWatch(kind);
        runInAction(() => {
          this.pauseKind = null;
          this.coachLine = null;
        });
        if (host.isCallLive()) await host.endCall();
        return;
      }

      const demo = fresh.demo;
      if (demo) host.setDemoInfo(demo);
      if (demo && this.resyncHold(kind, demo)) return;

      // Without a server deadline a release could end a call the server still considers live.
      const deadlineAt = demo?.deadline_at;
      if (!deadlineAt) {
        this.armRecoveryWatch(kind, RECOVERY_RETRY_MS[kind]);
        return;
      }
      if (kind === "rep") this.applyResume("auto", deadlineAt);
      else this.applyFreezeEnd("auto", deadlineAt);
    } catch {
      if (this.isHeldAs(kind)) this.armRecoveryWatch(kind, RECOVERY_RETRY_MS[kind]);
    }
  }

  private resyncHold(kind: HoldKind, demo: PublicSessionDemoBlock): boolean {
    if (kind === "rep") {
      if (!demo.paused_at || !demo.auto_resume_at) return false;
      this.beginPauseTracking(demo.paused_at, demo.auto_resume_at);
      return true;
    }
    if (demo.pause_kind !== "coach_freeze" || !demo.paused_at) return false;
    runInAction(() => {
      this.coachLine = demo.coach_line ?? "";
    });
    this.armFreezeRecoveryWatch(demo.paused_at);
    return true;
  }

  private disarmBoundaryWatch(): void {
    this.boundaryDispose?.();
    this.boundaryDispose = null;
    this.disarmBoundaryHardCap();
  }

  private disarmBoundaryHardCap(): void {
    if (this.boundaryHardCap) {
      clearTimeout(this.boundaryHardCap);
      this.boundaryHardCap = null;
    }
  }
}
